// SupportChat.jsx — flluska e asistentit AI për aplikacionin kryesor.
//
// PËRDORIMI (një rresht te src/App.jsx, brenda pemës kur përdoruesi është i
// loguar):
//   import SupportChat from "./SupportChat.jsx";
//   ...
//   {session && <SupportChat />}
//
// Vetë-mjaftueshme: lexon konfigurimin publik (rpc_chatbot_public_config) dhe
// shfaqet vetëm nëse asistenti është AKTIV. Dërgon mesazhet te Edge Function
// "chatbot" (JWT-ja shkon automatikisht përmes functions.invoke).

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "./supabaseClient";
import { SAFE_TOP, SAFE_BOTTOM } from "./zonaESigurt"; // kapaku 64/48 — shih shënimin atje

const ACCENT = "#4f46e5";

export default function SupportChat({ open: openProp, setOpen: setOpenProp, showLauncher = true }) {
  // 4 shtator 2026 — teksti këtu ishte i ngulitur shqip, pra dilte shqip edhe
  // te ndërfaqja angleze. Çelësat janë vetë fjalitë, si kudo tjetër.
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);           // { enabled, name, welcome_message, tone }
  // Mund të jetë i kontrolluar nga jashtë (butoni te menyja) ose vetjak (flluska).
  const [openInner, setOpenInner] = useState(false);
  const open = openProp !== undefined ? openProp : openInner;
  const setOpen = setOpenProp || setOpenInner;
  const [msgs, setMsgs] = useState([]);           // {role, content}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [convId, setConvId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    supabase.rpc("rpc_chatbot_public_config").then(({ data }) => {
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.enabled) setCfg(row);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && cfg && msgs.length === 0) {
      setMsgs([{ role: "assistant", content: cfg.welcome_message }]);
    }
  }, [open, cfg]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy, open]);

  // TE TELEFONI BISEDA MERR TË GJITHË EKRANIN.
  //
  // Një kuti 360×520 që noton mbi një ekran 390px të gjerë lë vend për katër
  // fjalë për rresht dhe e mbulon gjysmën e asaj që po shikon. Bisedë do të
  // thotë shkrim dhe lexim; ato duan hapësirë, jo një dritare të vogël me hije.
  // Te desktopi kutia mbetet e njëjtë — aty ka vend, dhe noton pa penguar.
  //
  // KËTA DY HOOK-A RRINË MBI `if (!cfg) return null`, DHE KJO KA RËNDËSI.
  // I pata vendosur poshtë tij. `cfg` mbërrin nga serveri: rendja e parë dilte
  // te ai rresht me katër hook-a, e dyta i thërriste gjashtë — dhe React-i e
  // ndal aplikacionin me gabimin #310. U duk vetëm kur konfigurimi mbërriti.
  const [ngushte, setNgushte] = useState(
    () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(max-width: 640px)").matches : false)
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 640px)");
    const on = () => setNgushte(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);

  if (!cfg) return null;

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("chatbot", {
        body: { message: text, conversationId: convId },
      });
      if (error) throw error;
      if (data?.conversationId) setConvId(data.conversationId);
      const reply = data?.reply || t("Sorry, something went wrong. Try again.");
      setMsgs((m) => [...m, { role: "assistant", content: reply }]);
    } catch (_e) {
      setMsgs((m) => [...m, { role: "assistant", content: t("Sorry, I could not connect. Try again in a moment.") }]);
    } finally {
      setBusy(false);
    }
  }

  const plot = ngushte && open;

  return (
    <div style={plot
      ? { position: "fixed", inset: 0, zIndex: 9000, fontFamily: "inherit", display: "flex", flexDirection: "column" }
      : { position: "fixed", right: 20, bottom: 20, zIndex: 9000, fontFamily: "inherit" }}>
      {open && (
        <div style={plot ? {
          flex: 1, minHeight: 0,
          background: "#fff", overflow: "hidden", display: "flex", flexDirection: "column",
          paddingTop: SAFE_TOP,
          paddingBottom: SAFE_BOTTOM,
        } : {
          width: 360, maxWidth: "calc(100vw - 40px)", height: 520, maxHeight: "calc(100vh - 120px)",
          background: "#fff", borderRadius: 18, overflow: "hidden", display: "flex", flexDirection: "column",
          boxShadow: "0 18px 50px rgba(15,23,42,.28), 0 4px 12px rgba(15,23,42,.16)",
          border: "1px solid #e6e8ec", marginBottom: 14, animation: "sc-in .22s cubic-bezier(.2,.8,.2,1) both",
        }}>
          <div style={{ background: `linear-gradient(135deg, ${ACCENT}, #7c74f0)`, color: "#fff", padding: "14px 16px", display: "flex", alignItems: "center", gap: 11 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 5h16v11H9l-5 4z" /></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{cfg.name}</div>
              <div style={{ fontSize: 11, opacity: .85 }}>{t("online · usually replies right away")}</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label={t("Close")} style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", padding: 0, width: 44, height: 44, margin: "-8px -10px -8px 0", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: 16, display: "flex", flexDirection: "column", gap: 10, background: "#f8fafc" }}>
            {msgs.map((m, i) => (
              <div key={i} style={{
                maxWidth: "82%", padding: "10px 13px", borderRadius: 14, fontSize: 13.5, lineHeight: 1.5, whiteSpace: "pre-wrap",
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                background: m.role === "user" ? ACCENT : "#fff",
                color: m.role === "user" ? "#fff" : "#0f172a",
                border: m.role === "user" ? "none" : "1px solid #e6e8ec",
                borderBottomRightRadius: m.role === "user" ? 4 : 14,
                borderBottomLeftRadius: m.role === "user" ? 14 : 4,
              }}>{m.content}</div>
            ))}
            {busy && (
              <div style={{ alignSelf: "flex-start", padding: "10px 14px", borderRadius: 14, background: "#fff", border: "1px solid #e6e8ec" }}>
                <span style={{ display: "inline-flex", gap: 4 }}>
                  <Dot d={0} /><Dot d={0.15} /><Dot d={0.3} />
                </span>
              </div>
            )}
          </div>

          <div style={{ padding: 12, borderTop: "1px solid #e6e8ec", display: "flex", gap: 9, background: "#fff" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder={t("Write a message…")}
              style={{ flex: 1, border: "1px solid #e6e8ec", background: "#f8fafc", borderRadius: 10, padding: "10px 13px", outline: "none", fontSize: 13.5, color: "#0f172a" }}
            />
            <button onClick={send} disabled={busy} aria-label={t("Send")} style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: 10, width: 42, cursor: busy ? "default" : "pointer", opacity: busy ? .6 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>
            </button>
          </div>
        </div>
      )}

      {showLauncher && (
        <button onClick={() => setOpen((o) => !o)} aria-label={t("Help")}
          style={{ display: plot ? "none" : "flex", width: 58, height: 58, borderRadius: "50%", border: "none", cursor: "pointer", marginLeft: "auto", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${ACCENT}, #7c74f0)`, color: "#fff", boxShadow: "0 10px 24px rgba(79,70,229,.45)", float: "right" }}>
          {open
            ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
            : <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M4 5h16v11H9l-5 4z" /><circle cx="9" cy="10.5" r="1" fill="currentColor" stroke="none" /><circle cx="13" cy="10.5" r="1" fill="currentColor" stroke="none" /><circle cx="17" cy="10.5" r="1" fill="currentColor" stroke="none" /></svg>}
        </button>
      )}

      <style>{`
        @keyframes sc-in { from { opacity: 0; transform: translateY(12px) scale(.98); } to { opacity: 1; transform: none; } }
        @keyframes sc-blink { 0%, 80%, 100% { opacity: .25; } 40% { opacity: 1; } }
      `}</style>
    </div>
  );
}

function Dot({ d }) {
  return <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#94a3b8", display: "inline-block", animation: `sc-blink 1.2s ${d}s infinite` }} />;
}
